import { Box, Stack } from "@mui/material";
import { type Bookings } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import { InHomeBookedVisitsContainer } from "../../InHome/BookedVisits/Container";
import { InHomeHighlightedBookedVisitsContainer } from "../../InHome/HighlightedBookedVisits/Container";
import { useGetWorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { EmptyPageState } from "./EmptyPageState";
import { HighlightedBookingSection } from "./HighlightedBookingSection";
import { LoadingPageState } from "./LoadingPageState";
import { ScheduledBookings } from "./ScheduledBookings";

interface BookingsContainerProps {
  isLoading: boolean;
  bookings: Bookings[];
  highlightedBooking?: Bookings;
  shiftPayBreakdown?: {
    hourlyPay?: string;
    totalPay?: string;
    totalWithBoostPay?: string;
  };
  currentDate: Date;
  showEmptyShiftState: boolean;
}

export function BookingsContainer(props: BookingsContainerProps) {
  const {
    isLoading,
    bookings,
    highlightedBooking,
    shiftPayBreakdown,
    currentDate,
    showEmptyShiftState,
  } = props;

  const shifts = useMemo(() => {
    return [
      ...(highlightedBooking?.type === "shift" ? [highlightedBooking.data] : []),
      ...bookings.filter((booking) => booking.type === "shift").map((booking) => booking.data),
    ];
  }, [bookings, highlightedBooking]);
  const { userId: workerId } = useDefinedWorker();

  const workersForOverlappingShiftSlots = useGetWorkersForOverlappingShiftSlots({
    shifts,
    selfWorkerId: workerId,
  });

  if (showEmptyShiftState) {
    return (
      <Box width="100%" height="100%" sx={{ marginTop: 4 }}>
        <EmptyPageState />
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: 4 }}>
      {isLoading && <LoadingPageState />}

      {!isLoading && (
        <Stack spacing={4}>
          <InHomeHighlightedBookedVisitsContainer />
          <HighlightedBookingSection
            highlightedBooking={highlightedBooking}
            shiftPayBreakdown={shiftPayBreakdown}
            currentDate={currentDate}
            workersForOverlappingShiftSlot={
              workersForOverlappingShiftSlots.overlappingShiftWorkersByShiftId[
                highlightedBooking?.type === "shift" ? highlightedBooking.data._id : ""
              ]
            }
          />
          <InHomeBookedVisitsContainer />

          <ScheduledBookings
            bookings={bookings}
            workersForOverlappingShiftSlotByShiftId={
              workersForOverlappingShiftSlots.overlappingShiftWorkersByShiftId
            }
          />
        </Stack>
      )}
    </Box>
  );
}
